// Firebase configuration for bridge requests
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  doc,
  updateDoc
} from 'firebase/firestore';

class FirebaseService {
  constructor() {
    this.initialized = false;
    this.db = null;
  }

  // Initialize Firebase
  async initialize() {
    try {
      console.log('🚀 Initializing Firebase...');
      
      const firebaseConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID
      };
      
      console.log('🔧 Firebase config:', {
        ...firebaseConfig,
        apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.substring(0, 6)}...` : 'MISSING'
      });
      
      const app = initializeApp(firebaseConfig);
      this.db = getFirestore(app);
      
      console.log('✅ Firebase service initialized successfully');
      this.initialized = true;
    } catch (error) {
      console.error('❌ Firebase initialization failed:', error);
      // Fallback to localStorage
      this.initialized = false;
    }
  }

  // Save bridge request for admin approval
  async saveBridgeRequest(bridgeData) {
    if (!this.initialized) {
      await this.initialize();
    }

    const bridgeRequest = {
      direction: bridgeData.direction, // 'bsc-to-solana' or 'solana-to-bsc'
      amount: bridgeData.amount,
      status: 'pending', // pending, approved, rejected, completed
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      adminNotes: '',
      bridgeFee: parseFloat(bridgeData.amount) * 0.001, // 0.1% fee
      netAmount: parseFloat(bridgeData.amount) * 0.999
    };

    // Add direction-specific fields
    if (bridgeData.direction === 'bsc-to-solana') {
      bridgeRequest.bscTxHash = bridgeData.bscTxHash;
      bridgeRequest.fromAddress = bridgeData.fromAddress;
      bridgeRequest.toSolanaAddress = bridgeData.toSolanaAddress;
      bridgeRequest.chainId = bridgeData.chainId;
      bridgeRequest.solanaTxHash = null; // Will be filled when admin sends tokens
    } else if (bridgeData.direction === 'solana-to-bsc') {
      bridgeRequest.solanaSignature = bridgeData.solanaSignature;
      bridgeRequest.fromSolanaAddress = bridgeData.fromSolanaAddress;
      bridgeRequest.toBSCAddress = bridgeData.toBSCAddress;
      bridgeRequest.bscTxHash = null; // Will be filled when admin sends tokens
    }

    try {
      if (!this.initialized) {
        console.log('🔄 Firebase not initialized for save, initializing now...');
        await this.initialize();
      }
      
      if (this.initialized && this.db) {
        console.log('💾 Saving bridge request to Firestore...');
        // Save to Firestore
        const docRef = await addDoc(collection(this.db, 'bridgeRequests'), bridgeRequest);
        bridgeRequest.id = docRef.id;
        console.log('✅ Bridge request saved to Firestore with ID:', bridgeRequest.id);
        return bridgeRequest;
      } else {
        console.log('💾 Saving bridge request to localStorage (fallback)...');
        // Fallback to localStorage
        bridgeRequest.id = `bridge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const existingRequests = await this.getAllBridgeRequests();
        existingRequests.push(bridgeRequest);
        localStorage.setItem('bridgeRequests', JSON.stringify(existingRequests));
        console.log('💾 Bridge request saved to localStorage with ID:', bridgeRequest.id);
        return bridgeRequest;
      }
    } catch (error) {
      console.error('❌ Error saving bridge request:', error);
      throw new Error('Failed to save bridge request');
    }
  }

  // Get all bridge requests (for admin dashboard)
  async getAllBridgeRequests() {
    console.log('📋 Getting all bridge requests...');
    console.log('Firebase initialized:', this.initialized);
    console.log('Database instance:', !!this.db);
    
    try {
      if (!this.initialized) {
        console.log('🔄 Firebase not initialized, initializing now...');
        await this.initialize();
      }
      
      if (this.initialized && this.db) {
        console.log('📡 Fetching from Firestore...');
        // Fetch from Firestore
        const q = query(collection(this.db, 'bridgeRequests'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const requests = [];
        querySnapshot.forEach((doc) => {
          requests.push({ id: doc.id, ...doc.data() });
        });
        console.log('✅ Fetched bridge requests from Firestore:', requests.length, 'requests');
        console.log('📊 Sample request:', requests[0] || 'No requests found');
        return requests;
      } else {
        console.log('💾 Using localStorage fallback...');
        // Fallback to localStorage
        const requests = localStorage.getItem('bridgeRequests');
        const parsedRequests = requests ? JSON.parse(requests) : [];
        console.log('💾 LocalStorage requests:', parsedRequests.length);
        return parsedRequests;
      }
    } catch (error) {
      console.error('❌ Error fetching bridge requests:', error);
      return [];
    }
  }

  // Get bridge requests by user address (either BSC or Solana address)
  async getBridgeRequestsByUser(userAddress) {
    try {
      console.log('🔍 Fetching requests for user:', userAddress);
      
      if (this.initialized && this.db) {
        console.log('✅ Using Firestore database');
        // Fetch from Firestore with user filtering
        const requests = [];
        
        try {
          // Query for BSC to Solana requests
          console.log('🔍 Querying BSC requests for:', userAddress.toLowerCase());
          const bscQuery = query(
            collection(this.db, 'bridgeRequests'),
            where('fromAddress', '==', userAddress.toLowerCase()),
            orderBy('createdAt', 'desc')
          );
          const bscSnapshot = await getDocs(bscQuery);
          console.log('📦 BSC query results:', bscSnapshot.size);
          bscSnapshot.forEach((doc) => {
            requests.push({ id: doc.id, ...doc.data() });
          });
        } catch (bscError) {
          console.warn('⚠️ BSC query failed:', bscError.message);
        }
        
        try {
          // Query for Solana to BSC requests
          console.log('🔍 Querying Solana requests for:', userAddress);
          const solanaQuery = query(
            collection(this.db, 'bridgeRequests'),
            where('fromSolanaAddress', '==', userAddress),
            orderBy('createdAt', 'desc')
          );
          const solanaSnapshot = await getDocs(solanaQuery);
          console.log('📦 Solana query results:', solanaSnapshot.size);
          solanaSnapshot.forEach((doc) => {
            requests.push({ id: doc.id, ...doc.data() });
          });
        } catch (solanaError) {
          console.warn('⚠️ Solana query failed:', solanaError.message);
        }
        
        // If queries failed, fall back to getting all requests and filtering
        if (requests.length === 0) {
          console.log('🔄 Falling back to client-side filtering');
          const allRequests = await this.getAllBridgeRequests();
          console.log('📊 Total requests to filter:', allRequests.length);
          
          const filteredRequests = allRequests.filter(request => {
            const match = (
              (request.direction === 'bsc-to-solana' && 
               request.fromAddress && 
               request.fromAddress.toLowerCase() === userAddress.toLowerCase()) ||
              (request.direction === 'solana-to-bsc' && 
               request.fromSolanaAddress === userAddress)
            );
            if (match) {
              console.log('✅ Found matching request:', request.id, request.direction);
            }
            return match;
          });
          
          console.log('🎯 Filtered requests:', filteredRequests.length);
          return filteredRequests;
        }
        
        // Remove duplicates and sort by creation date
        const uniqueRequests = requests.filter((request, index, self) => 
          index === self.findIndex(r => r.id === request.id)
        );
        uniqueRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        console.log('✅ Fetched user bridge requests from Firestore:', uniqueRequests.length);
        return uniqueRequests;
      } else {
        // Fallback to localStorage
        const allRequests = await this.getAllBridgeRequests();
        return allRequests.filter(request => {
          if (request.direction === 'bsc-to-solana') {
            return request.fromAddress && request.fromAddress.toLowerCase() === userAddress.toLowerCase();
          } else if (request.direction === 'solana-to-bsc') {
            return request.fromSolanaAddress === userAddress;
          }
          return false;
        });
      }
    } catch (error) {
      console.error('Error fetching user bridge requests:', error);
      return [];
    }
  }

  // Get bridge request by transaction hash (BSC or Solana)
  async getBridgeRequestByTxHash(txHash) {
    try {
      if (this.initialized && this.db) {
        // Query Firestore for the transaction hash
        const allRequests = await this.getAllBridgeRequests();
        return allRequests.find(request => 
          request.bscTxHash === txHash || request.solanaSignature === txHash
        );
      } else {
        // Fallback to localStorage
        const allRequests = await this.getAllBridgeRequests();
        return allRequests.find(request => 
          request.bscTxHash === txHash || request.solanaSignature === txHash
        );
      }
    } catch (error) {
      console.error('Error fetching bridge request by txHash:', error);
      return null;
    }
  }

  // Update bridge request status (for admin)
  async updateBridgeRequestStatus(requestId, status, adminNotes = '', completionTxHash = null) {
    console.log('🔄 Updating bridge request status:', { requestId, status, adminNotes, completionTxHash });
    try {
      const requests = await this.getAllBridgeRequests();
      console.log('📋 Retrieved requests for update:', requests.length);
      const requestIndex = requests.findIndex(r => r.id === requestId);
      console.log('🔍 Request index found:', requestIndex);
      
      if (requestIndex === -1) {
        console.error('❌ Bridge request not found with ID:', requestId);
        throw new Error('Bridge request not found');
      }

      const request = requests[requestIndex];
      console.log('📄 Found request to update:', request);
      const updatedRequest = {
        ...request,
        status,
        adminNotes,
        updatedAt: new Date().toISOString()
      };

      // Add completion transaction hash based on direction
      if (completionTxHash && status === 'completed') {
        if (request.direction === 'bsc-to-solana') {
          updatedRequest.solanaTxHash = completionTxHash;
        } else if (request.direction === 'solana-to-bsc') {
          updatedRequest.bscTxHash = completionTxHash;
        }
      }

      requests[requestIndex] = updatedRequest;

      // Update in Firebase if available, otherwise fallback to localStorage
      if (this.initialized && this.db) {
        console.log('💾 Updating request in Firestore...');
        // Update in Firestore
        const docRef = doc(this.db, 'bridgeRequests', requestId);
        await updateDoc(docRef, updatedRequest);
        console.log('✅ Request updated in Firestore');
      } else {
        console.log('💾 Updating request in localStorage (fallback)...');
        // Fallback to localStorage
        localStorage.setItem('bridgeRequests', JSON.stringify(requests));
        console.log('✅ Request updated in localStorage');
      }

      console.log('🎉 Bridge request updated successfully:', requests[requestIndex]);
      return requests[requestIndex];
    } catch (error) {
      console.error('Error updating bridge request:', error);
      throw error;
    }
  }

  // Get pending requests count (for admin)
  async getPendingRequestsCount() {
    const allRequests = await this.getAllBridgeRequests();
    return allRequests.filter(request => request.status === 'pending').length;
  }

  // Get statistics
  async getStatistics() {
    const allRequests = await this.getAllBridgeRequests();
    
    return {
      total: allRequests.length,
      pending: allRequests.filter(r => r.status === 'pending').length,
      approved: allRequests.filter(r => r.status === 'approved').length,
      completed: allRequests.filter(r => r.status === 'completed').length,
      rejected: allRequests.filter(r => r.status === 'rejected').length,
      totalVolume: allRequests.reduce((sum, r) => sum + parseFloat(r.amount), 0),
      totalFees: allRequests.reduce((sum, r) => sum + parseFloat(r.bridgeFee), 0)
    };
  }
}

// Export singleton instance
export const firebaseService = new FirebaseService();
export default firebaseService;
